import { format } from 'date-fns/format';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton } from 'rollup-allspark';
import ComponentPlaceholder from '../../../components/ComponentPlaceholder';
import { MULTI_LANG_TEXTS } from '../../../config/constants/translation-strings/analytics';
import { isEmpty } from '../../../utils';
import { abbreviateNumber, readablePrice } from '../../../utils/getCurrency';
import SectionCard from '../components/SectionCard';
import { KpiCard } from '../components';
import {
  generatePromoAnalyticsCSVData,
  generateXLSXDataFromCSVData,
} from '../../../utils/nativeAnalyticsUtils';
import {
  downloadCsvFromJSON,
  downloadXLSXFromJSON,
} from '../../../utils/sheetUtils';
import { makeSelectProperty } from '../selectors';

const PromoAnalytics = ({ promoSummary = {}, promoAnalytics = [] }) => {
  const { PROMO } = MULTI_LANG_TEXTS || {};
  const {
    PROMO_SUMMARY_TOTAL_PROMOS,
    PROMO_SUMMARY_ACTIVE_PROMOS,
    PROMO_TITLE,
    PROMO_TABLE_HEADER_NAME,
    PROMO_TABLE_HEADER_NET_REVENUE,
    PROMO_TABLE_HEADER_DISCOUNT_APPLIED,
    PROMO_TABLE_HEADER_TRANSATION_COUNT,
    PROMO_TABLE_HEADER_USERS_CLAIMED,
  } = PROMO || {};

  const csvHeaderData = {
    promo_code: 'Promo Name',
    gross_revenue: 'Gross Order Value / Net Sales',
    gross_revenue_cent: 'Gross Order Value / Net Sales (Cent)',
    discount: 'Discount Applied',
    discount_cent: 'Discount Applied (Cent)',
    total_orders: 'Total Orders',
    total_users: 'Users Claimed',
  };

  const handleCSVDownload = () => {
    const CSVData = generatePromoAnalyticsCSVData(
      csvHeaderData,
      promoAnalytics,
    );

    downloadCsvFromJSON(
      CSVData,
      `Promo-Analytics-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
    );
  };

  const handleXLSXDownload = () => {
    const CSVData = generatePromoAnalyticsCSVData(
      csvHeaderData,
      promoAnalytics,
    );
    const XLSXData = generateXLSXDataFromCSVData(CSVData);

    downloadXLSXFromJSON(
      XLSXData,
      `Promo-Analytics-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
    );
  };

  const kpiStripEl = (() => {
    if (isEmpty(promoSummary) && isEmpty(promoAnalytics)) return null;

    const symbol =
      (promoAnalytics[0] && promoAnalytics[0].currency_symbol) || '$';
    const money = cents => `${symbol}${abbreviateNumber((+cents || 0) / 100)}`;
    const sum = key =>
      promoAnalytics.reduce((acc, p) => acc + (+p[key] || 0), 0);

    const cards = [
      {
        label: PROMO_SUMMARY_TOTAL_PROMOS,
        value: promoSummary?.total_promos ?? 0,
      },
      {
        label: PROMO_SUMMARY_ACTIVE_PROMOS,
        value: promoSummary?.active_promos ?? 0,
      },
      { label: 'Promo Orders', value: sum('total_orders').toLocaleString() },
      { label: 'Users Claimed', value: sum('total_users').toLocaleString() },
      { label: 'Promo Revenue', value: money(sum('gross_revenue')) },
      { label: 'Discount Applied', value: money(sum('discount')) },
    ];

    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {cards.map(card => (
          <KpiCard key={card.label} label={card.label} value={card.value} />
        ))}
      </div>
    );
  })();

  const listEl = (() => {
    if (isEmpty(promoAnalytics)) {
      return (
        <div className="pb-6">
          <ComponentPlaceholder label="No promo analytics found" />
        </div>
      );
    }

    const headerCell =
      'w-1/5 px-4 py-3 flex items-center text-xs font-semibold uppercase text-grey-darker';
    const headerEl = (
      <div className="flex border-b border-grey">
        <div className={headerCell}>{PROMO_TABLE_HEADER_NAME}</div>
        <div className={`${headerCell} justify-end`}>
          {PROMO_TABLE_HEADER_NET_REVENUE}
        </div>
        <div className={`${headerCell} justify-end`}>
          {PROMO_TABLE_HEADER_DISCOUNT_APPLIED}
        </div>
        <div className={`${headerCell} justify-end`}>
          {PROMO_TABLE_HEADER_TRANSATION_COUNT}
        </div>
        <div className={`${headerCell} justify-end`}>
          {PROMO_TABLE_HEADER_USERS_CLAIMED}
        </div>
      </div>
    );

    const rowEl = promoAnalytics.map(promo => {
      const currency = {
        code: promo.currency,
        symbol: promo.currency_symbol,
      };

      return (
        <div
          key={promo.promo_id}
          className="flex border-b border-grey hover:bg-grey-lighter"
        >
          <div className="w-1/5 px-4 py-3 flex items-center">
            {promo.promo_code}
          </div>
          <div className="sc-tabular-nums w-1/5 px-4 py-3 flex items-center justify-end">
            {readablePrice(promo.gross_revenue, currency)}
          </div>
          <div className="sc-tabular-nums w-1/5 px-4 py-3 flex items-center justify-end">
            {readablePrice(promo.discount, currency)}
          </div>
          <div className="sc-tabular-nums w-1/5 px-4 py-3 flex items-center justify-end">
            {promo.total_orders}
          </div>
          <div className="sc-tabular-nums w-1/5 px-4 py-3 flex items-center justify-end">
            {promo.total_users}
          </div>
        </div>
      );
    });

    return (
      <div className="flex flex-col">
        {headerEl}
        {rowEl}
      </div>
    );
  })();

  const fileDownloadEl = !isEmpty(promoAnalytics) ? (
    <div className="flex items-center gap-x-2">
      <SCButton
        variant="primary-outline"
        size="sm"
        className="h-6 w-auto px-5 text-xs flex items-center gap-x-1"
        action={handleCSVDownload}
      >
        <i className="kt-download" />
        <p>CSV</p>
      </SCButton>
      <SCButton
        variant="primary-outline"
        size="sm"
        className="h-6 w-auto px-5 text-xs flex items-center gap-x-1"
        action={handleXLSXDownload}
      >
        <i className="kt-download" />
        <p>XLSX</p>
      </SCButton>
    </div>
  ) : null;

  return (
    <SectionCard title={PROMO_TITLE} actions={fileDownloadEl}>
      <div className="flex flex-col gap-y-6">
        {kpiStripEl}
        {listEl}
      </div>
    </SectionCard>
  );
};

PromoAnalytics.propTypes = {
  promoSummary: PropTypes.object,
  promoAnalytics: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  promoSummary: makeSelectProperty('promoSummary'),
  promoAnalytics: makeSelectProperty('promoAnalytics'),
});

const withConnect = connect(mapStateToProps, null);

export default compose(withConnect)(PromoAnalytics);
